import request from './httpService';

const CATALOG_API_ENDPOINT = '/catalog';

/**
 * Catalog Service - Access to global read-only product catalog
 */

// ===== Frames =====

/**
 * List frames with filters and pagination
 * @param {object} params - { page, pageSize, q, brandId, brandSlug, shape, material, gender, year, yearMin, yearMax, aMin, aMax, dblMin, dblMax, frameType, sort }
 */
const listFrames = (params, signal) => {
    return request({
        method: 'GET',
        url: `${CATALOG_API_ENDPOINT}/frames`,
        params,
        signal
    });
};

/**
 * Get frame by product ID
 * @param {string} id - catalog product UUID
 */
const getFrameById = (id, signal) => {
    return request({
        method: 'GET',
        url: `${CATALOG_API_ENDPOINT}/frames/${id}`,
        signal
    });
};

/**
 * Get frame by UPC/EAN code
 * @param {string} upc - UPC or EAN barcode
 */
const getFrameByUpc = (upc, signal) => {
    return request({
        method: 'GET',
        url: `${CATALOG_API_ENDPOINT}/frames/by-upc/${upc}`,
        signal
    });
};

/**
 * Find frames by model number (flexible search)
 * @param {string} model - model number (with or without spaces)
 * @param {object} options - { exact, brandId, brandSlug }
 */
const findFramesByModel = (model, options = {}, signal) => {
    return request({
        method: 'GET',
        url: `${CATALOG_API_ENDPOINT}/frames/by-model`,
        params: { model, ...options },
        signal
    });
};

/**
 * Create a new catalog frame product
 * @param {object} data - Frame product data
 */
const createFrameProduct = (data) => {
    return request({
        method: 'POST',
        url: `${CATALOG_API_ENDPOINT}/frames`,
        data
    });
};

/**
 * Import catalog frames from Excel/CSV file
 * @param {FormData} formData - File and mapping data
 */
const importFrames = (formData) => {
    return request({
        method: 'POST',
        url: `${CATALOG_API_ENDPOINT}/frames/import`,
        data: formData,
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

// ===== Filter Options =====

/**
 * Get catalog brands for filtering
 */
const getCatalogBrandsFilter = (signal) => {
    return request({
        method: 'GET',
        url: `${CATALOG_API_ENDPOINT}/filter-options/brands`,
        signal
    });
};

/**
 * Get frame shapes for filtering
 */
const getFrameShapesFilter = (signal) => {
    return request({
        method: 'GET',
        url: `${CATALOG_API_ENDPOINT}/filter-options/shapes`,
        signal
    });
};

/**
 * Get frame materials for filtering
 */
const getFrameMaterialsFilter = (signal) => {
    return request({
        method: 'GET',
        url: `${CATALOG_API_ENDPOINT}/filter-options/materials`,
        signal
    });
};

// ===== Import to Inventory =====

/**
 * Import a catalog product into tenant inventory
 * @param {object} data - { catalogProductId, supplierId, locationId, cost, retailPrice, quantity, notes }
 */
const importToInventory = (data) => {
    return request({
        method: 'POST',
        url: '/validdata/inventory/import-from-catalog',
        data
    });
};

/**
 * Copy a global frame to tenant catalog
 * @param {string} frameId - Global frame product ID
 * @param {object} overrides - Optional overrides { sku, modelNumber, etc. }
 */
const promoteFrame = (frameId, overrides = {}) => {
    return request({
        method: 'POST',
        url: `${CATALOG_API_ENDPOINT}/frames/${frameId}/promote`,
        data: overrides
    });
};

const bulkPromoteFrames = (ids = [], overrides = {}) => {
    return request({
        method: 'POST',
        url: `${CATALOG_API_ENDPOINT}/frames/promote`,
        data: { ids, overrides }
    });
};

const deleteFrame = (frameId) => {
    return request({
        method: 'DELETE',
        url: `${CATALOG_API_ENDPOINT}/frames/${frameId}`
    });
};

const deleteFrames = (ids = []) => {
    return request({
        method: 'DELETE',
        url: `${CATALOG_API_ENDPOINT}/frames`,
        data: { ids }
    });
};

/**
 * Update a tenant catalog frame
 * @param {string} frameId - Frame product ID
 * @param {object} data - Updated frame data
 */
const updateFrame = (frameId, data) => {
    return request({
        method: 'PUT',
        url: `${CATALOG_API_ENDPOINT}/frames/${frameId}`,
        data
    });
};

export const CatalogService = {
    listFrames,
    getFrameById,
    getFrameByUpc,
    findFramesByModel,
    createFrameProduct,
    importFrames,
    getCatalogBrandsFilter,
    getFrameShapesFilter,
    getFrameMaterialsFilter,
    importToInventory,
    promoteFrame,
    bulkPromoteFrames,
    deleteFrame,
    deleteFrames,
    updateFrame
};
